import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Route,
  Router,
  UrlSegment,
} from '@angular/router';
import { Observable, combineLatest, map } from 'rxjs';

import { Rights } from '@shared/enums';
import { SelectedSubproject } from '@shared/models';
import { UserDetailsFacade } from '@shared/state/facades';
import {
  canActivateAccountGuard,
  canActivateAppStatusGuard,
  canActivateClientGuard,
  canActivateSubprojectGuard,
  canMatchAccountGuard,
  canMatchClientGuard,
  canMatchSubprojectGuard,
} from '.';
import { CanActivateFn, CanMatchFn } from '@shared/models/guard';

export const canActivateProjectSetupGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot
): Observable<boolean> => {
  const userDetailsFacade = inject(UserDetailsFacade);
  const router = inject(Router);

  return combineLatest([
    canActivateAccountGuard(),
    canActivateClientGuard(),
    canActivateSubprojectGuard(route, null),
    canActivateAppStatusGuard(),
    userDetailsFacade.query.selectedSubproject$,
  ]).pipe(
    map(
      ([
        hasAccount,
        hasClient,
        hasSubprojectIdAndRights,
        isAppOnline,
        selectedSubproject,
      ]) =>
        hasPermissions(
          router,
          hasAccount,
          hasClient,
          hasSubprojectIdAndRights,
          isAppOnline,
          selectedSubproject
        )
    )
  );
};

export const canMatchProjectSetupGuard: CanMatchFn = (
  route: Route,
  segments: UrlSegment[]
): Observable<boolean> => {
  const userDetailsFacade = inject(UserDetailsFacade);
  const router = inject(Router);

  return combineLatest([
    canMatchAccountGuard(),
    canMatchClientGuard(),
    canMatchSubprojectGuard(route, segments),
    canActivateAppStatusGuard(),
    userDetailsFacade.query.selectedSubproject$,
  ]).pipe(
    map(
      ([
        hasAccount,
        hasClient,
        hasSubprojectIdAndRights,
        isAppOnline,
        selectedSubproject,
      ]) =>
        hasPermissions(
          router,
          hasAccount,
          hasClient,
          hasSubprojectIdAndRights,
          isAppOnline,
          selectedSubproject
        )
    )
  );
};

const hasPermissions = (
  router: Router,
  hasAccount: boolean,
  hasClient: boolean,
  hasSubprojectIdAndRights: boolean,
  isAppOnline: boolean,
  selectedSubproject: SelectedSubproject
): boolean => {
  if (
    !hasAccount ||
    !hasClient ||
    !hasSubprojectIdAndRights ||
    !isAppOnline ||
    selectedSubproject?.projectSetup === Rights.None
  ) {
    router.navigate(['/']);

    return false;
  }

  return true;
};
